import moment from "moment";
import { useTranslation } from "react-i18next";
import DateInput from "../../../../parts/inputs/date-input";

const dateFormat = {
  normal: "YYYY-MM-DD",
  calendar: "MMM D, YYYY",
  utc: "YYYYMMDD",
};

// The pickers take a real date. The range keeps its dates as text in the language of the page
// ("ene. 16, 2026"), which the picker's own text reader (English only) cannot read back for
// January, April, May, August and December.
const toDate = (value) =>
  value ? moment(value, dateFormat.calendar).toDate() : new Date();

const getSelectorsOptions = ({ quickDisabled, type, value, t }) => [
  {
    key: "2weeks",
    text: t("reports.rangeSelector.twoWeeks"),
    value: 2,
    type: "weeks",
    active: !quickDisabled && type === "weeks" && value === 2,
  },
  {
    key: "1month",
    text: t("reports.rangeSelector.oneMonth"),
    value: 1,
    type: "months",
    active: !quickDisabled && type === "months" && value === 1,
  },
  {
    key: "3month",
    text: t("reports.rangeSelector.threeMonths"),
    value: 3,
    type: "months",
    active: !quickDisabled && type === "months" && value === 3,
  },
  {
    key: "6month",
    text: t("reports.rangeSelector.sixMonths"),
    value: 6,
    type: "months",
    active: !quickDisabled && type === "months" && value === 6,
  },
  {
    key: "lifetime",
    text: t("reports.rangeSelector.lifetime"),
    value: 1,
    type: "lifetime",
    active: !quickDisabled && type === "lifetime",
  },
];

const Selector = ({
  text = "",
  type,
  value,
  active = false,
  activeButtonType = "primary",
  getDateFirstEntry,
  dateTo,
  updateState,
}) => (
  <button
    data-button-type={active ? activeButtonType : "secondary"}
    onClick={() => {
      if (type === "lifetime") {
        updateState({
          rangeSelector: {
            type,
            value,
          },
          quickDisabled: false,
          dateTo: null,
          dateFrom: moment(getDateFirstEntry()).format(dateFormat.calendar),
        });
        return;
      }
      const formatedDateTo = (
        dateTo
          ? moment(dateTo, dateFormat.calendar)
          : moment(new Date(), dateFormat.normal)
      )
        .subtract(value, type)
        .format(dateFormat.calendar);
      updateState({
        dateFrom: formatedDateTo,
        dateTo,
        quickDisabled: false,
        rangeSelector: {
          type,
          value,
        },
      });
    }}
    type="button"
  >
    <span>{text}</span>
  </button>
);

const RangeSelector = ({
  dateTo,
  rangeSelector,
  quickDisabled,
  updateState,
  getDateFirstEntry,
  selectorOptions,
  activeButtonType,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { value, type } = rangeSelector;
  const selectors =
    selectorOptions ?? getSelectorsOptions({ quickDisabled, type, value, t });

  return (
    <div className="selector-container">
      {selectors.map(({ text, type, value, active }) => (
        <Selector
          key={type + value}
          text={text}
          type={type}
          value={value}
          active={active}
          activeButtonType={activeButtonType}
          getDateFirstEntry={getDateFirstEntry}
          dateTo={dateTo}
          updateState={updateState}
        />
      ))}
    </div>
  );
};

const RangeContainerComponent = ({
  dateTo,
  dateFrom,
  rangeSelector,
  quickDisabled,
  updateState,
  getDateFirstEntry,
  selectorOptions,
  activeButtonType,
}) => {
  const { t } = useTranslation("athlete-portal");
  const handleDateChange = (date, type = "dateFrom") => {
    let arg = {
      [type]: date,
      quickDisabled: true,
    };

    if (
      type === "dateTo" &&
      moment(date, dateFormat.calendar).isBefore(toDate(dateFrom))
    ) {
      // If dateTo is before than dateFrom, update dateFrom
      arg = Object.assign({}, arg, {
        dateFrom: date,
      });
    }

    if (type === "dateFrom" && !dateTo) {
      arg = Object.assign({}, arg, {
        dateTo: moment(new Date()).format(dateFormat.calendar),
      });
    }

    updateState(arg);
  };

  return (
    <section role="date-range-container">
      <div role="calendar-date-selectors">
        <DateInput
          name="from"
          placeholder={t("reports.startDate")}
          value={toDate(dateFrom)}
          max={toDate(dateTo)}
          onChange={(date) => handleDateChange(date, "dateFrom")}
        />
        <span>{t("reports.to")}</span>
        <DateInput
          name="to"
          placeholder={t("reports.endDate")}
          value={toDate(dateTo)}
          max={new Date()}
          onChange={(date) => handleDateChange(date, "dateTo")}
        />
      </div>
      <RangeSelector
        dateTo={dateTo}
        rangeSelector={rangeSelector}
        quickDisabled={quickDisabled}
        updateState={updateState}
        getDateFirstEntry={getDateFirstEntry}
        selectorOptions={selectorOptions}
        activeButtonType={activeButtonType}
      />
    </section>
  );
};
export default RangeContainerComponent;
